import {
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  TreeSelect,
} from "antd";
import { DataNode } from "antd/es/tree";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DataFormat } from "utils/format/DataFormat";
import { NumberFormat } from "utils/format/NumberFormat";
import { SelectOps } from "../selectComponent/model/SelectOpsModel";
import SelectTableInput from "../selectTable/SelectTableInput";
import { FilterAttributes } from "./model/FilterComponentsModel";

export const FilterComponent = <T extends FilterAttributes[]>(
  data: T,
  form?: FormInstance,
) => {
  const cachedCapability = productCache
    ?.getCache(CacheKeyEnum.capabilities)
    ?.map((item) => item?.key);
  return (
    <>
      {data?.map((item, index) => {
        const isWithCapability =
          item?.needCapaChecking &&
          cachedCapability?.some((child) =>
            [
              `${CapabilityEnum.filterPrefix}${item?.filterName}`,
              item?.capabilityKey,
            ]?.includes(child),
          );
        if (!item?.needCapaChecking || isWithCapability) {
          return (
            <Col
              span={item?.colDefault ? item?.colDefault : 24}
              lg={item?.colLg}
              key={index}
            >
              <div className="truncate">{item?.labelName}</div>
              {item?.filterType === "select-multiple" ? (
                <Form.Item
                  name={item?.filterName}
                  initialValue={item?.defaultValue}
                >
                  <Select
                    mode="multiple"
                    loading={item?.loading || false}
                    disabled={item?.disable ?? false}
                    defaultValue={item?.defaultValue as string[]}
                    showSearch
                    filterOption={
                      item?.filterOption === false
                        ? false
                        : (input, option) =>
                            DataFormat.selectFilterOption(
                              input,
                              option,
                              !!item?.customKey && item?.customKey,
                            )
                    }
                    options={item?.options as SelectOps[]}
                    placeholder={item?.placeholder}
                    maxTagCount={"responsive"}
                    onPopupScroll={item?.onPopupScroll}
                    onSearch={item?.onSearch}
                    onBlur={item?.onBlur}
                    onOpenChange={item?.onOpenChange}
                    suffixIcon={item?.suffixIcon}
                    onChange={(selected) => {
                      if (item?.onChange) {
                        item?.onChange(selected);
                      } else {
                        DataFormat.handleMultipleChange(
                          selected,
                          item?.filterName,
                          form,
                        );
                      }
                    }}
                  />
                </Form.Item>
              ) : item?.filterType === "input" ? (
                <Form.Item name={item?.filterName}>
                  <Input
                    style={{ width: "100%" }}
                    disabled={item?.disable ?? false}
                    placeholder={item?.placeholder}
                    prefix={item?.prefixIcon}
                    type={item?.inputType || ""}
                    maxLength={!!item?.maxLength ? item?.maxLength : undefined}
                  />
                </Form.Item>
              ) : item?.filterType === "select" ? (
                <Form.Item name={item?.filterName}>
                  <Select
                    defaultValue={item?.defaultValue}
                    showSearch
                    loading={item?.loading || false}
                    disabled={item?.disable ?? false}
                    styles={{
                      popup: {
                        root: {
                          maxHeight: 400,
                          overflow: "auto",
                        },
                      },
                    }}
                    filterOption={
                      item?.filterOption === false
                        ? false
                        : (input, option) =>
                            DataFormat.selectFilterOption(
                              input,
                              option,
                              !!item?.customKey && item?.customKey,
                            )
                    }
                    options={item?.options as SelectOps[]}
                    onPopupScroll={item?.onPopupScroll}
                    onSearch={item?.onSearch}
                    onBlur={item?.onBlur}
                    onOpenChange={item?.onOpenChange}
                    suffixIcon={item?.suffixIcon}
                    onChange={(value) =>
                      !!item?.onChange ? item?.onChange(value) : null
                    }
                  />
                </Form.Item>
              ) : item?.filterType === "range-date" ? (
                <Form.Item
                  name={item?.filterName}
                  initialValue={item?.defaultRangeDay}
                >
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    allowClear={false}
                    disabled={item?.disable ?? false}
                    defaultValue={item?.defaultRangeDay}
                    onChange={(value) =>
                      !!item?.onChange ? item?.onChange(value) : null
                    }
                    format={"DD/MM/YYYY"}
                  />
                </Form.Item>
              ) : item?.filterType === "select-table" ? (
                <Form.Item name={item?.filterName}>
                  <SelectTableInput
                    {...item?.selectTableProps}
                    disable={item?.disable}
                  />
                </Form.Item>
              ) : item?.filterType === "price-number" ? (
                <Form.Item
                  name={item?.filterName}
                  initialValue={item?.defaultValue}
                >
                  <InputNumber<number>
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    type="tel"
                    parser={(value) =>
                      value?.replace(/\$\s?|(,*)/g, "") as unknown as number
                    }
                    onKeyDown={NumberFormat.handleKeyDown}
                    placeholder={item?.placeholder}
                    className="w-full"
                    suffix={
                      item?.needCurrency === true ? (
                        <span className="font-medium text-green-700">VND</span>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Form.Item>
              ) : (
                <Form.Item name={item?.filterName}>
                  <TreeSelect
                    style={{ width: "100%" }}
                    disabled={item?.disable ?? false}
                    defaultValue={item?.defaultValue}
                    loading={item?.loading || false}
                    showSearch
                    filterTreeNode={(inputValue, treeNode) => {
                      if (typeof treeNode.title === "string") {
                        return treeNode.title
                          .toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }
                      return false;
                    }}
                    treeData={item?.options as DataNode[]}
                    placeholder={item?.placeholder}
                    treeDefaultExpandAll
                  />
                </Form.Item>
              )}
            </Col>
          );
        } else {
          return null;
        }
      })}
    </>
  );
};
